import { inject, Injectable, signal } from '@angular/core';
import {
  FirestoreAdapter,
  FirestoreAdapterCollection,
  DocumentData,
  DocumentReference,
  QueryFn,
} from '@shared/firestore/firestore-adapter';
import {
  catchError,
  combineLatest,
  finalize,
  firstValueFrom,
  lastValueFrom,
  map,
  of,
} from 'rxjs';
import { MembershipStore } from '@shared/store/membership';
import { RedeemActivityStore } from '@shared/store/redeemActivity';

import { ILocation } from '@models/ILocation';
import {
  BillingTermEnum,
  IMembership,
  IRedeemActivity,
} from '@models/IMembership';
import { CreateStripeProductPayload } from '@models/IStripeAccount';
import { SharedService } from '@services/shared.service';
import { StripeService } from '@services/stripe.service';
import { SubjectTypesEnum } from '../enums/subject-types-enum';
import { CompanyStore } from '@shared/store/company';

/** Stopgap cap on large report queries until report-specific pagination lands. */
const REPORTS_QUERY_LIMIT = 500;

@Injectable({
  providedIn: 'root',
})
export class MembershipService {
  #membershipRef: FirestoreAdapterCollection<IMembership>;
  #firestore = inject(FirestoreAdapter);
  #sharedService = inject(SharedService);
  #stripeService = inject(StripeService);
  #membershipStore = inject(MembershipStore);
  #redeemActivityStore = inject(RedeemActivityStore);
  #companyStore = inject(CompanyStore);
  membershipsCount = 0;

  constructor() {
    this.#membershipRef =
      this.#firestore.collection<IMembership>('memberships');
  }

  async addMembership(membership: IMembership) {
    this.#membershipStore.setLoading(true);
    if (membership.mostPopular && !membership.companyId) {
      this.#membershipStore.setLoading(false);
      throw new Error(
        'A company is required to mark a membership as most popular.',
      );
    }

    const firebasePayload: IMembership = {
      ...membership,
      createdOn: new Date().toISOString(),
      deleted: false,
    };

    const res = await this.#membershipRef.add(firebasePayload);
    this.membershipsCount++;
    const stripePayload: CreateStripeProductPayload = {
      billingTerm: membership.billingTerm,
      description: membership.description,
      name: membership.name,
      price: membership.price,
      membershipId: res.id,
    };

    let result: { success: boolean };
    try {
      result = await lastValueFrom(
        this.#stripeService.createProduct(stripePayload),
      );
    } catch (error) {
      this.#membershipStore.setLoading(false);
      this.#sharedService.logErrors(
        error,
        'addMembership',
        'MembershipService',
      );
      await this.#membershipRef.doc(res.id).delete();
      throw error;
    }

    try {
      if (membership.mostPopular && membership.companyId) {
        await this.#setMostPopularMembership(res.id, membership.companyId);
      }
      return result;
    } catch (error) {
      this.#sharedService.logErrors(
        error,
        'setMostPopularMembership',
        'MembershipService',
      );
      if (error instanceof Error) {
        this.#membershipStore.setError(error.message);
      }

      throw error;
    } finally {
      this.#membershipStore.setLoading(false);
    }
  }

  async #setMostPopularMembership(
    membershipId: string,
    companyId: DocumentReference,
    targetData: Partial<IMembership> = {},
  ): Promise<void> {
    const query: QueryFn<DocumentData> = (ref) =>
      ref.where('companyId', '==', companyId);
    const snapshot = await firstValueFrom(
      this.#firestore.collection<IMembership>('memberships', query).get(),
    );
    const target = snapshot.docs.find((doc) => doc.id === membershipId);

    if (!target) {
      throw new Error('The most popular membership could not be found.');
    }

    const batch = this.#firestore.firestore.batch();
    batch.update(target.ref, { ...targetData, mostPopular: true });

    snapshot.docs.forEach((doc) => {
      if (doc.id !== membershipId && doc.data().mostPopular) {
        batch.update(doc.ref, { mostPopular: false });
      }
    });

    await batch.commit();

    this.#membershipStore.setMembershipsById(
      snapshot.docs
        .filter((doc) => !doc.data().deleted)
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
          ...(doc.id === membershipId ? targetData : {}),
          mostPopular: doc.id === membershipId,
        })),
    );
  }

  compareMembershipFields(data: Partial<IMembership>, membership: IMembership) {
    const changedPrice = data.price && membership.price !== data.price;

    const changedTerm =
      data.billingTerm && membership.billingTerm !== data.billingTerm;

    const changedName = data.name && membership.name !== data.name;

    const changedDescription =
      data.description && membership.description !== data.description;

    const changedActive =
      typeof data.active === 'boolean' && membership.active !== data.active;

    return {
      newPrice: changedPrice || changedTerm,
      newInfo: changedName || changedDescription || changedActive,
    };
  }

  async updateMembership(
    id: string,
    updatedBy: string,
    data: Partial<IMembership>,
  ) {
    this.#membershipStore.setLoading(true);
    try {
      const membershipRef = this.#firestore.doc<IMembership>(
        `memberships/${id}`,
      );
      const membership = (await lastValueFrom(membershipRef.get())).data();
      const updateData: Partial<IMembership> = {
        updatedOn: new Date().toISOString(),
        updatedBy,
        ...data,
      };

      if (!membership) {
        this.#membershipStore.setLoading(false);
        return;
      }

      if (data.mostPopular) {
        const companyId = data.companyId ?? membership.companyId;
        if (!companyId) {
          throw new Error(
            'A company is required to mark a membership as most popular.',
          );
        }
        await this.#setMostPopularMembership(id, companyId, updateData);
      } else {
        await membershipRef.update(updateData);
      }

      const { newPrice, newInfo } = this.compareMembershipFields(
        data,
        membership,
      );

      let newPriceObservable = of({ success: true });
      let newInfoObservable = of({ success: true });

      if (newPrice) {
        newPriceObservable = this.#stripeService.newProductPrice({
          billingTerm: data.billingTerm || membership.billingTerm,
          price: data.price || membership.price,
          membershipId: id,
        });
      }

      if (newInfo) {
        newInfoObservable = this.#stripeService.updateProductInfo({
          name: data.name || membership.name,
          description: data.description || membership.description,
          membershipId: id,
          active:
            typeof data.active === 'boolean' ? data.active : membership.active,
        });
      }

      const results = await lastValueFrom(
        combineLatest([newPriceObservable, newInfoObservable]),
      );
      this.#membershipStore.setLoading(false);

      return {
        success: results.every((r) => r.success),
      };
    } catch (error) {
      this.#sharedService.logErrors(
        error,
        'updateMembership',
        'MembershipService',
      );
      if (error instanceof Error) {
        this.#membershipStore.setError(error?.message || '');
      }
      this.#membershipStore.setLoading(false);
      throw error;
    }
  }

  async deleteMembership(id: string) {
    await this.#firestore
      .doc<IMembership>(`memberships/${id}`)
      .update({ deleted: true })
      .catch((error) => {
        this.#sharedService.logErrors(
          error,
          'deleteMembership',
          'MembershipService',
        );
        this.#membershipStore.setError(error.message);
      });

    this.membershipsCount--;
    this.#membershipStore.deleteMembership(id);
    return;
  }

  async getMembershipsByAssociatedLocationId(
    locationId: string,
    visibleToCustomers: boolean | null = null,
  ): Promise<IMembership[]> {
    this.#sharedService.currentRequests++;
    this.#membershipStore.setLoading(true);
    try {
      const locRef: DocumentReference<ILocation> =
        this.#firestore.doc<ILocation>(`locations/${locationId}`).ref;

      const query: QueryFn = (ref) => {
        const baseQuery = ref
          .where('associatedLocations', 'array-contains', locRef)
          .where('deleted', '==', false)
          .where('active', '==', true)
          .where('stripeConnectedAccountId', '>', '');
        if (visibleToCustomers !== null) {
          return baseQuery.where(
            'visibleToCustomers',
            '==',
            visibleToCustomers,
          );
        }
        return baseQuery;
      };

      const snapshot = await firstValueFrom(
        this.#firestore.collection<IMembership>('memberships', query).get(),
      );

      const memberships = snapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as IMembership,
      );

      // check that the memberships have stripe info
      const filteredMemberships = memberships.filter(
        (m) => m.stripePriceId && m.stripeProductId,
      );

      this.#membershipStore.setMembershipsById(filteredMemberships);

      this.membershipsCount = filteredMemberships.length;
      this.#sharedService.emitSubject({
        type: SubjectTypesEnum.membershipsByLocationIdReady,
        data: filteredMemberships,
      });
      return filteredMemberships;
    } catch (err) {
      this.membershipsCount = 0;
      this.#sharedService.logErrors(
        err,
        'getMembershipsByAssociatedLocation',
        'MembershipService',
      );
      if (err instanceof Error) {
        this.#membershipStore.setError(err?.message || '');
      }

      return [];
    } finally {
      this.#sharedService.currentRequests--;
      this.#membershipStore.setLoading(false);
    }
  }

  getAllMemberships() {
    this.#sharedService.currentRequests++;
    this.#membershipStore.setLoading(true);
    const companyRef = this.#firestore.doc(
      `companies/${this.#companyStore.company()?.id}`,
    ).ref;
    const query: QueryFn<DocumentData> = (ref) =>
      ref.where('deleted', '==', false).where('companyId', '==', companyRef);

    return this.#firestore
      .collection<IMembership>('memberships', query)
      .get()
      .pipe(
        catchError((err) => {
          this.membershipsCount = 0;
          this.#membershipStore.setError(err.message);
          this.#sharedService.logErrors(
            err,
            'getAllMemberships',
            'MembershipService',
          );
          return of(null);
        }),
        map((snapshot) => {
          this.#membershipStore.setLoading(false);
          if (!snapshot) {
            return [];
          }

          const memberships: IMembership[] = snapshot.docs.map(
            (doc) => ({ id: doc.id, ...doc.data() }) as IMembership,
          );

          return memberships;
        }),
        // Single decrement for the one increment above, on every path.
        finalize(() => this.#sharedService.currentRequests--),
      )
      .subscribe((memberships) => {
        this.membershipsCount = memberships.length;
        this.#membershipStore.setMembershipsById(memberships);
      });
  }
  async getMembershipsByCustomer(customerId: string): Promise<IMembership[]> {
    this.#sharedService.currentRequests++;
    this.#membershipStore.setLoading(true);
    try {
      const query: QueryFn<DocumentData> = (ref) =>
        ref.where('customerId', '==', customerId).where('deleted', '!=', true);
      const snapshot = await firstValueFrom(
        this.#firestore.collection<IMembership>('memberships', query).get(),
      );

      const memberships: IMembership[] = snapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as IMembership,
      );
      this.membershipsCount = memberships.length;

      return memberships;
    } catch (err) {
      this.membershipsCount = 0;
      this.#sharedService.logErrors(
        err,
        'getMembershipsByCustomer',
        'MembershipService',
      );
      this.#membershipStore.setError(err.message);
      return [];
    } finally {
      this.#membershipStore.setLoading(false);
      this.#sharedService.currentRequests--;
    }
  }
  getMembership(id: string) {
    return this.#firestore
      .doc<IMembership>(`memberships/${id}`)
      .snapshotChanges()
      .pipe(
        catchError((error) => {
          this.#sharedService.logErrors(
            error,
            'getMembership',
            'MembershipService',
          );
          this.#membershipStore.setError(error.message);
          return of(null);
        }),
        map((m) => {
          if (!m?.payload?.exists) {
            return null;
          }
          return { id: m.payload.id, ...m.payload.data() } as IMembership;
        }),
      );
  }

  getMembershipsActivity() {
    this.#membershipStore.setLoading(true);
    const companyRef = this.#firestore.doc(
      `companies/${this.#companyStore.company()?.id}`,
    ).ref;

    return this.#firestore
      .collection<IRedeemActivity>('redeemRegistry', (ref) =>
        ref
          .orderBy('timestamp', 'desc')
          .where('companyId', '==', companyRef)
          .limit(REPORTS_QUERY_LIMIT),
      )
      .snapshotChanges()
      .pipe(
        catchError((error) => {
          this.#membershipStore.setError(error.message);
          this.#sharedService.logErrors(
            error,
            'getMembershipsActivity',
            'MembershipService',
          );
          return of(null);
        }),
        map((snapShotChanges) => {
          if (!snapShotChanges) return [];
          return snapShotChanges.map((snapShotChange) => ({
            id: snapShotChange.payload.doc.id,
            ...snapShotChange.payload.doc.data(),
          }));
        }),
      )
      .subscribe((activities) => {
        this.#membershipStore.setLoading(false);
        this.#redeemActivityStore.setRedeemActivities(activities || []);
      });
  }
}
